import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, Input } from 'reactstrap';
import { removeSessionKey } from '../../api/Common';
import { GlobalContext } from '../../api/GlobalContext';
import APP_VERSION from '../../api/AppVersion';
import EditableLabel from '../../components/Misc/EditableLabel';
import {
    EMR_OPTIONS,
    PROFESSION_OPTIONS,
    SPECIALITY_OPTIONS,
} from '../../constants/EmrNoteTypeMapping';

const SAVE_DELAY_MS = 800;

const Profile = ({ userDetails }) => {
  const { apiClient } = useContext(GlobalContext);
  const [name, setName] = useState('');
  const [profession, setProfession] = useState('');
  const [signature, setSignature] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [emr, setEmr] = useState('');
  const [providerID, setProviderID] = useState('');
  const [baaAgreementUrl, setBaaAgreementUrl] = useState('');
  const [clickCount, setClickCount] = useState(0);
  const payloadRef = useRef({});
  const debounceTimer = useRef(null);
  const apiClientRef = useRef(apiClient);

  useEffect(() => {
    apiClientRef.current = apiClient;
  }, [apiClient]);

  useEffect(() => {
    setName(userDetails?.name || '');
    setProfession(userDetails?.profession || '');
    setSignature(userDetails?.signature || '');
    setSpeciality(userDetails?.speciality || '');
    setEmr(userDetails?.EMR || '');
    setProviderID(userDetails?.provider_id || '');
  }, [userDetails]);

  const scheduleSave = () => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const payload = payloadRef.current;
      if (Object.keys(payload).length > 0) {
        payloadRef.current = {};
        apiClientRef.current.editUserSettings(payload).catch((error) =>
          console.error('Error updating user settings:', error),
        );
      }
    }, SAVE_DELAY_MS);
  };

  // Helper function to get profession options with existing value if not in list
  const getProfessionOptions = () => {
    const options = [...PROFESSION_OPTIONS];
    if (profession && !PROFESSION_OPTIONS.includes(profession)) {
      options.unshift(profession);
    }
    return options;
  };

  // Helper function to get speciality options with existing value if not in list
  const getSpecialityOptions = () => {
    const options = [...SPECIALITY_OPTIONS];
    if (speciality && !SPECIALITY_OPTIONS.includes(speciality)) {
      options.unshift(speciality);
    }
    return options;
  };

  // Helper function to get EMR options with existing value if not in list
  const getEmrOptions = () => {
    const options = [...EMR_OPTIONS];
    if (emr && !EMR_OPTIONS.includes(emr)) {
      options.unshift(emr);
    }
    return options;
  };

  // Flush any pending save on unmount
  useEffect(() => {
    return () => {
      clearTimeout(debounceTimer.current);
      const payload = payloadRef.current;
      if (Object.keys(payload).length > 0) {
        apiClientRef.current.editUserSettings(payload).catch(() => {});
      }
    };
  }, []);

  // Fetch agreement URL when the link is clicked
  const handleClick = () => {
    apiClient
      .getAgreements('Business Associate Agreement')
      .then((agreementDetails) => {
        const agreementUrl = agreementDetails?.url;
        setBaaAgreementUrl(agreementUrl);
        if (agreementUrl) {
          window.location.href = agreementUrl;
        }
      })
      .catch((error) => {
        console.error('Error fetching agreement details:', error);
      });
  };

  const itemStyle = {
    marginBottom: '20px',
  };

  const dropdownStyle = {
    width: '100%',
    minWidth: '150px',
    resize: 'none',
    overflow: 'auto',
    border: 'none',
    background: 'transparent',
    outline: 'none',
    boxShadow: 'none',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    fontFamily: 'inherit',
    textAlign: 'left',
    padding: '5px 0 12px 0',
    margin: '0',
  };

  function handleTextChange(newValue, oldValue = '', type) {
    if (newValue === oldValue) return;

    const newPayload = { ...payloadRef.current };

    if (type === 'name') {
      if (newValue.length === 0) return;
      setName(newValue);
      newPayload.name = newValue;
      userDetails.name = newValue;
    }
    if (type === 'profession') {
      if (newValue.toLowerCase() === 'beta') {
        if (!apiClientRef.current.is_beta) {
          alert('Welcome to the beta program! Refresh to get the goodies!');
        }
        apiClientRef.current.is_beta = true;
        apiClientRef.current.editUserSettings({ is_beta: true });
        return;
      }
      setProfession(newValue);
      newPayload.professional_details = {
        ...newPayload.professional_details,
        profession: newValue,
      };
      userDetails.profession = newValue;
    }
    if (type === 'speciality') {
      setSpeciality(newValue);
      newPayload.professional_details = {
        ...newPayload.professional_details,
        speciality: newValue,
      };
      userDetails.speciality = newValue;
    }
    if (type === 'signature') {
      setSignature(newValue);
      newPayload.professional_details = {
        ...newPayload.professional_details,
        signature: newValue,
      };
      userDetails.signature = newValue;
    }
    if (type === 'emr') {
      setEmr(newValue);
      newPayload.professional_details = {
        ...newPayload.professional_details,
        EMR: newValue,
      };
      userDetails.emr = newValue;
    }
    if (type === 'providerID') {
      setProviderID(newValue);
      newPayload.professional_details = {
        ...newPayload.professional_details,
        provider_id: newValue,
      };
      userDetails.providerID = newValue;
    }

    payloadRef.current = newPayload;
    scheduleSave();
  }

  const email = 'contact@comprehendpt.ai';
  const subject = 'Account Deletion Request';
  const body = 'Please delete my account.';
  const handleDeleteClick = () => {
    const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailLink, '_blank');
  };

  const handleActiveSinceClick = () => {
    setClickCount((prevCount) => {
      const newCount = prevCount + 1;
      if (newCount === 8) {
        localStorage.setItem('is_dev', 'true');
        alert('You are now a developer!');
        console.log('is_dev set to true');
      }
      return newCount;
    });
  };

  return (
    <div>
      <div>
        <div style={itemStyle}>
          <strong>Email:</strong> {userDetails?.email}
        </div>
        <div style={itemStyle}>
          <strong>Active Since:</strong>{' '}
          <span onClick={handleActiveSinceClick}>
            {userDetails?.createdAt?.match(/\d{4}-\d{2}-\d{2}/)[0]}
          </span>
        </div>
        <div style={itemStyle}>
          <strong>Version:</strong> {APP_VERSION}
        </div>
        <div style={itemStyle}>
          <strong>Name: </strong>
          <EditableLabel
            text={name}
            onChange={(newValue, oldValue) =>
              handleTextChange(newValue, oldValue, 'name')
            }
            className="h3"
            placeholder="Name cannot be empty"
          />
        </div>
        <div style={itemStyle}>
          <strong>Profession: </strong>
          <div
            className="editableLabel"
            style={{
              marginBottom: '10px',
              border: '3px solid $primary',
              flexGrow: 1,
            }}
          >
            <Input
              type="select"
              value={profession}
              onChange={(e) => {
                const newValue = e.target.value;
                setProfession(newValue);
                handleTextChange(newValue, profession, 'profession');
              }}
              className="h3"
              style={{
                ...dropdownStyle,
                color: profession ? 'black' : '#8898aa',
              }}
            >
              <option value="">Select Profession</option>
              {getProfessionOptions().map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Input>
          </div>
        </div>
        <div style={itemStyle}>
          <strong>Speciality: </strong>
          <div
            className="editableLabel"
            style={{
              marginBottom: '10px',
              border: '3px solid $primary',
              flexGrow: 1,
            }}
          >
            <Input
              type="select"
              value={speciality}
              onChange={(e) => {
                const newValue = e.target.value;
                setSpeciality(newValue);
                handleTextChange(newValue, speciality, 'speciality');
              }}
              className="h3"
              style={{
                ...dropdownStyle,
                color: speciality ? 'black' : '#8898aa',
              }}
            >
              <option value="">Select Speciality</option>
              {getSpecialityOptions().map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Input>
          </div>
        </div>
        <div style={itemStyle}>
          <strong>EMR System: </strong>
          <div
            className="editableLabel"
            style={{
              marginBottom: '10px',
              border: '3px solid $primary',
              flexGrow: 1,
            }}
          >
            <Input
              type="select"
              value={emr}
              onChange={(e) => {
                const newValue = e.target.value;
                setEmr(newValue);
                handleTextChange(newValue, emr, 'emr');
              }}
              className="h3"
              style={{
                ...dropdownStyle,
                color: emr ? 'black' : '#8898aa',
              }}
            >
              <option value="">Select EMR System</option>
              {getEmrOptions().map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Input>
          </div>
        </div>
        <div style={itemStyle}>
          <strong>Signature: </strong>
          <EditableLabel
            text={signature}
            onChange={(newValue, oldValue) =>
              handleTextChange(newValue, oldValue, 'signature')
            }
            className="h3"
          />
        </div>
        <div style={itemStyle}>
          <strong>Provider ID: </strong>
          <EditableLabel
            text={providerID}
            onChange={(newValue, oldValue) =>
              handleTextChange(newValue, oldValue, 'providerID')
            }
            className="h3"
          />
        </div>
        <div style={itemStyle}>
          <Link
            to="/auth/reset/email"
            onClick={() => removeSessionKey(apiClient)}
          >
            {' '}
            Reset Password{' '}
          </Link>
        </div>
        <div style={itemStyle}>Service Agreement</div>
        <Link to={baaAgreementUrl} onClick={handleClick}>
          Business Associate Agreement
        </Link>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '20px', // Added margin for spacing
        }}
      >
        <Button color="primary" size="sm" onClick={handleDeleteClick}>
          Delete Account
        </Button>
      </div>
    </div>
  );
};

export default Profile;
